import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { ColorProps, SpaceProps } from 'styled-system';

// Components.
import Box from '../Box';
import { Close } from "@mui/icons-material";

// Styles.
import {
  Container,
  Overlay,
  CloseBox,
  ScrollableContent
} from './SideSheet.style';

// Utils.
import { useESCKey } from '../../../utils/hooks';

export interface ISideSheetProps extends React.HTMLProps<HTMLDivElement> {
  /**
   * Whether the side sheet is open or not.
   */
  isOpen: boolean;

  /**
   * Event handler for closing the side sheet, since the
   * side sheet does not maintain its own internal state.
   */
  onRequestClose: () => void;

  /**
   * Custom React component to render as the close tab
   */
  closeBox?: React.ReactElement;
}

/**
 * Container for content that appears on the right of the
 * page in a modal context. Content underneath is not accessible.
 */
const SideSheet: React.FC<ISideSheetProps & ColorProps & SpaceProps> = ({
  isOpen,
  children,
  onRequestClose,
  closeBox: customCloseBox,
  ...rest
}) => {
  /**
   * Effect to prevent scrolling on the overlay when SideSheet is open
   */
  React.useLayoutEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : 'auto';

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  /**
   * Handler for when the user clicks on the overlay.
   */
  const handleOverlayClick = (): void => {
    onRequestClose();
  };

  /**
   * Close the popover when user hits `esc` key.
   */
  useESCKey(() => {
    onRequestClose();
  });

  const closeBox = React.isValidElement(customCloseBox) ? (
    customCloseBox
  ) : (
    <CloseBox
      as="button"
      position="absolute"
      height="33px"
      width="33px"
      backgroundColor="transparent"
      padding={0}
      alignItems="center"
      justifyContent="center"
      color="grayScale.11"
      zIndex={4}
      id="btn-side_sheet-close"
    >
      <Close />
    </CloseBox>
  );

  return (
    <>
      {isOpen &&
        ReactDOM.createPortal(
          <>
            <Overlay onClick={handleOverlayClick} />
            <Container
              position="absolute"
              color="primary.0"
              backgroundColor="grayScale.0"
            >
              <Box
                position="relative"
                color="grayScale.11"
                onClick={() => onRequestClose()}
              >
                {closeBox}
              </Box>

              <ScrollableContent {...(rest as any)}>
                {children}
              </ScrollableContent>
            </Container>
          </>,
          document.body
        )}
    </>
  );
};

export default SideSheet;
